/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Injectable, Inject } from '@angular/core';
import { ApimProxyConfig } from './models/apim-config.model';
import { APIM_CONFIG_TOKEN } from './apim-config.token';

/**
 * Service for managing APIM proxy configuration with validation
 *
 * This service provides:
 * - Configuration validation on initialization
 * - Immutable configuration access
 * - Type-safe configuration retrieval
 * - Environment-based configuration support
 *
 * The configuration is validated on service construction and frozen to prevent
 * accidental modifications.
 *
 * @example
 * ```typescript
 * constructor(private configService: ApimConfigService) {
 *   const config = this.configService.getConfig();
 *   console.log('APIM Base URL:', config.baseUrl);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ApimConfigService {
  private readonly config: Readonly<ApimProxyConfig>;

  constructor(@Inject(APIM_CONFIG_TOKEN) config: ApimProxyConfig) {
    this.validateConfig(config);
    this.config = Object.freeze({ ...config });
  }

  /**
   * Retrieves the immutable APIM proxy configuration
   *
   * @returns Readonly configuration object
   */
  getConfig(): Readonly<ApimProxyConfig> {
    return this.config;
  }

  /**
   * Gets the base URL for APIM proxy
   *
   * @returns APIM proxy base URL
   */
  getBaseUrl(): string {
    return this.config.baseUrl;
  }

  /**
   * Gets the APIM subscription key
   *
   * @returns APIM subscription key
   */
  getSubscriptionKey(): string {
    return this.config.apimSubscriptionKey;
  }

  /**
   * Gets the request timeout in milliseconds
   *
   * @returns Request timeout value
   */
  getRequestTimeout(): number {
    return this.config.requestTimeout;
  }

  /**
   * Validates the configuration object
   * Throws an error if configuration is invalid
   *
   * @param config - Configuration to validate
   * @throws Error if configuration is invalid
   */
  private validateConfig(config: ApimProxyConfig): void {
    const errors: string[] = [];

    if (!config.baseUrl || config.baseUrl.trim() === '') {
      errors.push('baseUrl is required and cannot be empty');
    }

    if (!config.apimSubscriptionKey || config.apimSubscriptionKey.trim() === '') {
      errors.push('apimSubscriptionKey is required and cannot be empty');
    }

    if (config.baseUrl && !this.isValidUrl(config.baseUrl)) {
      errors.push('baseUrl must be a valid URL');
    }

    if (typeof config.requestTimeout !== 'number' || config.requestTimeout <= 0) {
      errors.push('requestTimeout must be a positive number');
    }

    if (errors.length > 0) {
      throw new Error(`APIM Configuration validation failed:\n${errors.join('\n')}`);
    }
  }

  /**
   * Validates if a string is a valid URL
   *
   * @param urlString - URL string to validate
   * @returns True if valid URL
   */
  private isValidUrl(urlString: string): boolean {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
